import { useState, useEffect } from "react";
import axios from "axios";
import { Key, Eye, EyeOff, RefreshCw, Save, CheckCircle2, AlertCircle, Loader2, Database, Cloud } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SOURCE_BADGE = {
  database: { label: "DB", className: "bg-green-500/15 text-green-400 border-green-500/30", icon: Database },
  env:      { label: ".env", className: "bg-blue-500/15 text-blue-400 border-blue-500/30", icon: Cloud },
  missing:  { label: "MISSING", className: "bg-red-500/15 text-red-400 border-red-500/30", icon: AlertCircle },
};

const ServiceCard = ({ service, token, onSaved }) => {
  const [drafts, setDrafts] = useState({});         // { fieldName: newValue }
  const [revealed, setRevealed] = useState({});      // { fieldName: true }
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);   // { ok, message }

  const dirty = Object.keys(drafts).length > 0;

  const handleProbe = async () => {
    setProbing(true);
    setProbeResult(null);
    try {
      const res = await axios.get(`${API}/admin/service-tokens/${service.service}/probe`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProbeResult({ ok: res.data.ok, message: res.data.message });
    } catch (e) {
      setProbeResult({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setProbing(false);
    }
  };

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    setProbeResult(null);
    try {
      const res = await axios.patch(
        `${API}/admin/service-tokens/${service.service}`,
        { credentials: drafts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProbeResult({ ok: res.data.probe_ok, message: res.data.probe_message });
      if (res.data.probe_ok) {
        toast.success(`${service.display_name}: saved + verified`);
      } else {
        toast.warning(`${service.display_name}: saved but probe failed — check the message`);
      }
      setDrafts({});
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDrafts({});
    setProbeResult(null);
  };

  return (
    <div className="bg-[#1a1c1f] rounded-xl border border-white/10 overflow-hidden" data-testid={`service-card-${service.service}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/5 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-mono text-base font-semibold" data-testid={`service-name-${service.service}`}>
            {service.display_name}
          </h3>
          <p className="text-[#ebebeb]/50 text-xs font-mono mt-1">{service.description}</p>
          {service.updated_at && (
            <p className="text-[#ebebeb]/30 text-[10px] font-mono mt-1">
              Last rotated by {service.updated_by || "system"} on {new Date(service.updated_at).toLocaleString()}
            </p>
          )}
        </div>
        <button
          onClick={handleProbe}
          disabled={probing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono border border-white/10 text-[#ebebeb]/70 hover:bg-white/5 disabled:opacity-50"
          data-testid={`probe-btn-${service.service}`}
        >
          {probing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Test
        </button>
      </div>

      {/* Fields */}
      <div className="px-5 py-4 space-y-3">
        {service.fields.map((f) => {
          const isRevealed = revealed[f.name];
          const isDraft = drafts[f.name] !== undefined;
          const SourceIcon = SOURCE_BADGE[f.source].icon;
          return (
            <div key={f.name} className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-2 items-center">
              <label className="text-[#ebebeb]/60 text-xs font-mono">
                {f.label}
                <span className="text-[#ebebeb]/30 text-[10px] block">{f.name}</span>
              </label>
              <div className="flex items-start gap-2">
                {f.is_multiline ? (
                  <textarea
                    value={isDraft ? drafts[f.name] : (isRevealed ? f.preview.replace(/[•…]/g, "") : f.preview)}
                    onChange={(e) => setDrafts({ ...drafts, [f.name]: e.target.value })}
                    onFocus={() => {
                      if (f.is_secret && !isDraft && !isRevealed) {
                        setDrafts({ ...drafts, [f.name]: "" });
                      }
                    }}
                    placeholder={f.is_set ? (f.is_secret ? "•••••• (click to replace with a fresh JSON key)" : f.preview) : "Paste service-account JSON here"}
                    rows={6}
                    className={`flex-1 bg-[#0f1012] border rounded-lg px-3 py-2 text-[11px] font-mono text-[#ebebeb] outline-none transition-colors resize-y min-h-[110px] ${
                      isDraft ? "border-[#ff3737]/40 bg-[#ff3737]/5" : "border-white/10 focus:border-[#ff3737]/40"
                    } ${f.is_secret && !isRevealed && !isDraft ? "text-transparent placeholder:text-[#ebebeb]/40" : ""}`}
                    data-testid={`field-input-${service.service}-${f.name}`}
                    autoComplete="off"
                    spellCheck="false"
                  />
                ) : (
                  <input
                    type={f.is_secret && !isRevealed ? "password" : "text"}
                    value={isDraft ? drafts[f.name] : (isRevealed ? f.preview.replace(/[•…]/g, "") : f.preview)}
                    onChange={(e) => setDrafts({ ...drafts, [f.name]: e.target.value })}
                    onFocus={(e) => {
                      // First focus on a redacted field: clear so user can paste fresh value
                      if (f.is_secret && !isDraft && !isRevealed) {
                        setDrafts({ ...drafts, [f.name]: "" });
                      }
                    }}
                    placeholder={f.is_set ? (f.is_secret ? "•••••• (click to replace)" : f.preview) : "Not set"}
                    className={`flex-1 bg-[#0f1012] border rounded-lg px-3 py-1.5 text-xs font-mono text-[#ebebeb] outline-none transition-colors ${
                      isDraft ? "border-[#ff3737]/40 bg-[#ff3737]/5" : "border-white/10 focus:border-[#ff3737]/40"
                    }`}
                    data-testid={`field-input-${service.service}-${f.name}`}
                    autoComplete="off"
                    spellCheck="false"
                  />
                )}
                {f.is_secret && (
                  <button
                    onClick={() => setRevealed({ ...revealed, [f.name]: !isRevealed })}
                    className="p-1.5 text-[#ebebeb]/40 hover:text-white"
                    data-testid={`reveal-${service.service}-${f.name}`}
                  >
                    {isRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded border flex items-center gap-1 ${SOURCE_BADGE[f.source].className}`}>
                <SourceIcon className="w-2.5 h-2.5" /> {SOURCE_BADGE[f.source].label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {(dirty || probeResult) && (
        <div className="px-5 py-3 border-t border-white/5 bg-white/[0.02] flex items-center justify-between gap-3">
          {probeResult ? (
            <div className={`flex items-center gap-1.5 text-xs font-mono ${probeResult.ok ? "text-green-400" : "text-red-400"}`} data-testid={`probe-result-${service.service}`}>
              {probeResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              {probeResult.message}
            </div>
          ) : <span className="text-[#ff3737]/80 text-xs font-mono">{Object.keys(drafts).length} field{Object.keys(drafts).length !== 1 ? "s" : ""} edited</span>}
          {dirty && (
            <div className="flex items-center gap-2">
              <button onClick={handleDiscard} disabled={saving} className="px-3 py-1.5 rounded text-xs font-mono text-[#ebebeb]/70 hover:bg-white/5" data-testid={`discard-${service.service}`}>
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 rounded text-xs font-mono bg-[#ff3737] text-white hover:bg-[#ff3737]/80 disabled:opacity-50 flex items-center gap-1.5"
                data-testid={`save-${service.service}`}
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Save & Verify
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ServiceTokensTab = ({ token }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/service-tokens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServices(res.data);
    } catch (e) {
      toast.error(`Failed to load services: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServices(); }, []);

  return (
    <>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Key className="w-6 h-6 text-[#ff3737]" />
          <h2 className="text-2xl font-bold text-white font-mono" data-testid="service-tokens-heading">Service Tokens</h2>
        </div>
        <p className="text-[#ebebeb]/60 font-mono text-sm">
          Rotate third-party SSP credentials at runtime — no redeploy required. Each save triggers a live API probe to confirm the new credentials work before they go live in the daily cron.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="service-tokens-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#ff3737]" />
        </div>
      ) : (
        <div className="space-y-4">
          {services.map((svc) => (
            <ServiceCard key={svc.service} service={svc} token={token} onSaved={fetchServices} />
          ))}
        </div>
      )}
    </>
  );
};

export default ServiceTokensTab;
